import { Component, Prop, h, Host } from '@stencil/core';
import { assetUrl } from '../../../../../utils/helpers/asset-path';

const COMPONENT_NAME = 'sn-long-content-type-1';

// Fixed product illustration. It MUST go through assetUrl(): a hardcoded
// root-relative path (`/signnow/...`) only works in the SSR HTML, where the asset
// rewriter upgrades it to the absolute CDN URL. The component's own render still
// emits the bare path, so the moment it re-renders on hydration the rewritten
// srcset/src are overwritten and the images 404 against the site root.
const IMAGE = 'signnow/long-contant-type-1/form-description-feature';
const IMAGE_WEBP = assetUrl(`${IMAGE}.webp`);
const IMAGE_WEBP_2X = assetUrl(`${IMAGE}@2x.webp`);
const IMAGE_PNG = assetUrl(`${IMAGE}.png`);
const IMAGE_PNG_2X = assetUrl(`${IMAGE}@2x.png`);

/**
 * How-to / full description section for SignNow forms pages. Displays a fixed product
 * illustration image on the left (desktop only, 960px+) and structured content on the right.
 * Supports raw HTML content from the `fulldescription` backend block via the
 * `rawHtml` prop or the `body` slot.
 * Uses a light neutral background.
 *
 * @product signnow
 * @source fulldescription
 * @version 6.0.0
 * @category SignNow
 * @slot heading - <me-heading variant="3" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading text
 * @slot subtitle - <me-text> - Section subtitle text below the heading
 * @slot body - {raw-html} - Raw HTML content (used when rawHtml prop is not provided)
 * @example
 *   <sn-long-content-type-1
 *     raw-html="<h2>What is the rental agreement?</h2><p>A rental agreement...</p>"
 *   >
 *     <me-heading slot="heading" variant="3" tag="h2">What is the rental agreement?</me-heading>
 *   </sn-long-content-type-1>
 */
@Component({
  tag: 'sn-long-content-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnLongContentType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Raw HTML content from the `fulldescription` backend data block.
   * When provided, rendered as the main content body.
   * Takes precedence over the `body` slot.
   * @content
   */
  @Prop() rawHtml?: string;

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME}>
          <div class={`${COMPONENT_NAME}__container`}>
            <div class={`${COMPONENT_NAME}__media`}>
              <picture class={`${COMPONENT_NAME}__picture`}>
                <source
                  type="image/webp"
                  srcSet={`${IMAGE_WEBP} 1x, ${IMAGE_WEBP_2X} 2x`}
                />
                <img
                  class={`${COMPONENT_NAME}__image`}
                  src={IMAGE_PNG}
                  srcSet={`${IMAGE_PNG_2X} 2x`}
                  alt=""
                  loading="lazy"
                  width="448"
                  height="556"
                />
              </picture>
            </div>
            <div class={`${COMPONENT_NAME}__content`}>
              <slot name="heading"></slot>
              <slot name="subtitle"></slot>
              {this.rawHtml
                ? <div class={`${COMPONENT_NAME}__body`} innerHTML={this.rawHtml}></div>
                : <slot name="body"></slot>}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
